import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { computeEffectivePermissions, computeEffectiveRoles } from "@/lib/rbacMatrix";

/**
 * ELIA · Founder profile switcher hook.
 * The founder can view the platform as any of these role personas without
 * logging out. The "effective role" drives the sidebar, guards, and API calls.
 *
 * Non-founder users cannot switch — their real role is always effective.
 */

export const ROLE_PERSONAS = [
  { key: "founder",     label: "Fondateur",       tone: "text-[#7A5B14]",  path: "/app" },
  { key: "admin",       label: "Administrateur",  tone: "text-[#0B2545]",  path: "/app/admin" },
  { key: "user",        label: "Membre",          tone: "text-[#0B2545]",  path: "/app" },
  { key: "pro",         label: "Professionnel",   tone: "text-[#065F46]",  path: "/app/mentor" },
  { key: "institution", label: "Institution",     tone: "text-[#5C1A5C]",  path: "/app/partner" },
];

const STORAGE_ROLE = "elia_active_role";
const STORAGE_TOKEN = "elia_impersonate_token";
const STORAGE_TOKEN_EXP = "elia_impersonate_exp";
const EVENT = "elia:role:changed";

export function getActiveRole(user) {
  if (typeof window === "undefined") return null;
  const roles = new Set([...(user?.roles || []), ...(user?.real_roles || [])]);
  const stored = localStorage.getItem(STORAGE_ROLE);
  // Only founders can maintain a stored persona
  if (roles.has("founder") && stored) return stored;
  if (roles.has("founder")) return "founder";
  // Priority for non-founders
  if (roles.has("admin") || roles.has("super_admin")) return "admin";
  if (roles.has("institution")) return "institution";
  if (roles.has("professional") || roles.has("professionnel") || roles.has("mentor") || roles.has("conseiller") || roles.has("partner")) return "pro";
  return "user";
}

export function getImpersonateToken() {
  if (typeof window === "undefined") return null;
  const tok = localStorage.getItem(STORAGE_TOKEN);
  const exp = parseInt(localStorage.getItem(STORAGE_TOKEN_EXP) || "0", 10);
  if (!tok || !exp) return null;
  if (Date.now() / 1000 > exp) {
    localStorage.removeItem(STORAGE_TOKEN);
    localStorage.removeItem(STORAGE_TOKEN_EXP);
    return null;
  }
  return tok;
}

function _clearImpersonate() {
  localStorage.removeItem(STORAGE_TOKEN);
  localStorage.removeItem(STORAGE_TOKEN_EXP);
}

/** Founder-only: request an impersonation JWT and switch UI mode. */
export async function setActiveRole(key, user) {
  if (typeof window === "undefined") return;
  const realRoles = new Set([...(user?.roles || []), ...(user?.real_roles || [])]);
  const isFounderUser = realRoles.has("founder") || realRoles.has("super_admin");
  if (!isFounderUser) return;

  if (key === "founder") {
    _clearImpersonate();
    localStorage.setItem(STORAGE_ROLE, "founder");
    window.dispatchEvent(new Event(EVENT));
    return;
  }

  try {
    const { data } = await api.post("/auth/impersonate", { persona: key });
    localStorage.setItem(STORAGE_TOKEN, data.token);
    localStorage.setItem(STORAGE_TOKEN_EXP, String(data.expires_at_epoch));
    localStorage.setItem(STORAGE_ROLE, key);
    window.dispatchEvent(new Event(EVENT));
  } catch {
    // Fall back to cosmetic-only switch (client-side rbac still gates the UI)
    localStorage.setItem(STORAGE_ROLE, key);
    window.dispatchEvent(new Event(EVENT));
  }
}

/**
 * Main hook exposing the effective role/permissions.
 * When impersonating: returns the persona's role + client-mirrored permissions.
 * When not impersonating: returns the real role + real permissions from the API.
 */
export function useActiveRole(user) {
  const [role, setRole] = useState(() => getActiveRole(user));

  const refresh = useCallback(() => {
    setRole(getActiveRole(user));
  }, [user]);

  useEffect(() => {
    const on = () => refresh();
    window.addEventListener(EVENT, on);
    refresh();
    return () => window.removeEventListener(EVENT, on);
  }, [refresh]);

  const realRoles = new Set([...(user?.roles || []), ...(user?.real_roles || [])]);
  const isFounderFlag = realRoles.has("founder") || realRoles.has("super_admin");
  const impersonating = isFounderFlag && role !== "founder";

  const effectiveRoles = computeEffectiveRoles(
    user?.real_roles?.length ? user.real_roles : (user?.roles || []),
    impersonating ? role : null,
  );
  const effectivePermissions = computeEffectivePermissions(
    user?.permissions || [], impersonating ? role : null,
  );

  return {
    active: role,
    impersonating,
    isFounder: isFounderFlag,
    effectiveRoles,
    effectivePermissions,  // Set<string>
  };
}

export function isFounder(user) {
  const roles = new Set([...(user?.roles || []), ...(user?.real_roles || [])]);
  return roles.has("founder") || roles.has("super_admin");
}
